// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import { type TakeAVideoConfig } from "@clipboard-health/contract-backend-main";
import {
  BottomSheet,
  BottomSheetLoadingContent,
  type UseModalState,
  useModalState,
} from "@clipboard-health/ui-components";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { ClockOutChartingReminder } from "@src/appV2/redesign/Charting/ClockOutChartingReminder";
import { useSkippableWait } from "@src/appV2/redesign/utils/useSkippableWait";
import {
  getWorkerShiftPath,
  type GetWorkerShiftResponse,
} from "@src/appV2/redesign/WorkerShifts/api/useGetWorkerShift";
import { DEFAULT_TIMECLOCK_COMPLIANCE_VIDEO_CONFIG } from "@src/appV2/Shifts/Shift/ShiftState/api/useGetShiftState";
import { type ShiftStateData } from "@src/appV2/Shifts/Shift/ShiftState/types";
import type { NfcTagRequest, WorkerShift } from "@src/appV2/Shifts/Shift/types";
import { useGetShiftFeedbackClockOutConfig } from "@src/appV2/Shifts/Shift/useGetShiftFeedbackClockOutConfig";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { ShiftMarkedNonIpReasons, useMarkShiftNonInstant } from "../../api/useMarkShiftNonInstant";
import {
  ActionCheckType,
  ShiftStage,
  useRecordShiftTimekeepingAction,
} from "../../api/useRecordShiftTimekeepingAction";
import { SHIFT_DETAILS_FULL_PATH, SHIFT_DETAILS_SUBMIT_TIMESHEET_PATH } from "../../path";
import { NfcHashValidationAction } from "../NfcValidation/api/types";
import { WorkerNfcValidation } from "../NfcValidation/WorkerNfcValidation";
import type { ComplianceProofVideo } from "../TimeclockComplianceVideoProof";
import { TimeclockVideoProofFlow } from "../TimeclockComplianceVideoProof";
import { AcceptNonInstantPayContent } from "./AcceptNonInstantPayContent";
import { ClockOutConfirmation } from "./ClockOutConfirmation";
import { DURATION_OF_TIMEKEEPING_ACTION_SUCCESS_MESSAGE_IN_MS } from "./constants";
import { NfcNotSupportedContent } from "./NfcNotSupportedContent";
import { TimekeepingActionSuccessContent } from "./TimekeepingActionSuccessContent";

export interface ClockOutTimeclockComplianceBottomSheetProps {
  modalState: UseModalState;
  shift: Pick<WorkerShift, "_id">;
  attendanceVerificationMethod: TakeAVideoConfig;
  workerCredentials: {
    pin: string;
  };
  workerId: string;
  workplaceId: string;
  workplaceName: string;
  isNfcEnabled: boolean;
  nfcTagRequests: NfcTagRequest[];
  shiftState: ShiftStateData;
}

type TimeclockComplianceStep =
  | "charting-reminder"
  | "clock-out-confirm"
  | "video-recording"
  | "nfc-validation"
  | "nfc-not-supported"
  | "requesting-clock-out"
  | "clock-out-success"
  | "accept-non-instant-pay"
  | "requesting-non-instant-pay";

export function ClockOutTimeclockComplianceBottomSheet(
  props: ClockOutTimeclockComplianceBottomSheetProps
) {
  const {
    modalState,
    workerCredentials,
    workerId,
    workplaceId,
    shift,
    workplaceName,
    isNfcEnabled,
    nfcTagRequests,
    shiftState,
    attendanceVerificationMethod,
  } = props;

  const shiftId = shift._id;

  const history = useHistory();
  const { showErrorToast } = useToast();
  const queryClient = useQueryClient();

  const chartingReminderModalState = useModalState();
  const { chartingRecallEnabled: shouldShowChartingReminder } =
    useGetShiftFeedbackClockOutConfig(workplaceId);

  const firstStep: TimeclockComplianceStep = shouldShowChartingReminder
    ? "charting-reminder"
    : "clock-out-confirm";

  const [step, setStep] = useState<TimeclockComplianceStep>(firstStep);
  const complianceProofRef = useRef<ComplianceProofVideo>();

  useEffect(() => {
    if (modalState.modalIsOpen && step === "charting-reminder") {
      chartingReminderModalState.openModal();
    }
  }, [modalState.modalIsOpen, step, chartingReminderModalState]);

  const { mutateAsync: recordTimekeepingAction } = useRecordShiftTimekeepingAction();

  const { wait, skip: skipWait } = useSkippableWait();

  const { mutateAsync: markNonInstant } = useMarkShiftNonInstant();

  const shiftDetailsPath = generatePath(SHIFT_DETAILS_FULL_PATH, {
    shiftId,
  });
  const submitTimesheetPath = `${shiftDetailsPath}${SHIFT_DETAILS_SUBMIT_TIMESHEET_PATH}`;

  const closeBottomSheet = useCallback(() => {
    skipWait();
    modalState.closeModal();
    chartingReminderModalState.closeModal();
    setStep(firstStep);
  }, [modalState, chartingReminderModalState, skipWait, firstStep]);

  async function handleRecordClockOut(
    actionCheckType: ActionCheckType,
    videoProof: ComplianceProofVideo | undefined
  ) {
    setStep("requesting-clock-out");

    try {
      const postMutationShift = await recordTimekeepingAction({
        shiftId,
        stage: ShiftStage.CLOCK_OUT,
        shiftActionCheck: actionCheckType,
        complianceProof: videoProof,
      });

      setStep("clock-out-success");
      await wait(DURATION_OF_TIMEKEEPING_ACTION_SUCCESS_MESSAGE_IN_MS);
      closeBottomSheet();

      queryClient.setQueryData<GetWorkerShiftResponse>([getWorkerShiftPath(shiftId)], (result) =>
        result
          ? {
              ...result,
              response: {
                ...result.response,
                clockInOut: postMutationShift.data.clockInOut,
              },
            }
          : undefined
      );

      history.replace(submitTimesheetPath, { clockedOut: true });
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to clock out, please try again.";

      logEvent(APP_V2_USER_EVENTS.CLOCK_OUT_FAILED, {
        shiftId,
        workerId,
        workplaceId,
        clockOutType: "timeclock-compliance",
        error: errorMessage,
      });

      setStep("accept-non-instant-pay");
    }
  }

  async function convertToNonInstantPay() {
    try {
      await markNonInstant({
        shiftId,
        reason: ShiftMarkedNonIpReasons.TIMECLOCK_VIDEO_PROOF_COMPLIANCE_FAILURE,
      });

      modalState.closeModal();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to convert to non-instant pay, please try again.";
      showErrorToast(errorMessage);

      closeBottomSheet();
    }
  }

  function handleCancel() {
    closeBottomSheet();
  }

  if (step === "charting-reminder" && shouldShowChartingReminder) {
    return (
      <ClockOutChartingReminder
        modalState={chartingReminderModalState}
        shiftId={shiftId}
        workplaceId={workplaceId}
        onConfirmClockOut={() => {
          setStep("clock-out-confirm");
          chartingReminderModalState.closeModal();
        }}
        onCancel={() => {
          closeBottomSheet();
        }}
      />
    );
  }

  if (step === "clock-out-confirm" && modalState.modalIsOpen) {
    return (
      <ClockOutConfirmation
        modalState={modalState}
        onConfirm={() => {
          setStep("video-recording");
        }}
        onCancel={closeBottomSheet}
      />
    );
  }

  if (step === "video-recording") {
    return (
      <TimeclockVideoProofFlow
        workplaceName={workplaceName}
        timeclockLocation={attendanceVerificationMethod.timeclockLocationString}
        timeclockPin={workerCredentials.pin}
        timeclockGifUrl={attendanceVerificationMethod.timeclockGifUrl}
        workplaceId={workplaceId}
        shiftId={shiftId}
        workerId={workerId}
        clockAction="clock-out"
        videoConfig={DEFAULT_TIMECLOCK_COMPLIANCE_VIDEO_CONFIG}
        onSuccess={async (videoProof: ComplianceProofVideo) => {
          complianceProofRef.current = videoProof;

          if (isNfcEnabled) {
            setStep("nfc-validation");
          } else {
            await handleRecordClockOut(ActionCheckType.LOCATION, videoProof);
          }
        }}
        onCancel={() => {
          handleCancel();
        }}
      />
    );
  }

  return (
    <BottomSheet disableSwipeArea modalState={modalState} canDismiss={false}>
      {step === "nfc-validation" && (
        <WorkerNfcValidation
          nfcTagRequests={nfcTagRequests}
          shiftId={shiftId}
          metaProps={{
            workerId,
            workplaceId,
            workplaceName,
          }}
          clockAction={NfcHashValidationAction.CLOCK_OUT}
          isMissedPunchRequestEnabled={shiftState.metadata.isMissedPunchRequestEnabled}
          onSuccess={async () => {
            await handleRecordClockOut(ActionCheckType.NFC, complianceProofRef.current);
          }}
          onNfcNotSupported={() => {
            setStep("nfc-not-supported");
          }}
          onSkipLocationCheck={() => {
            setStep("accept-non-instant-pay");
          }}
          onCancel={() => {
            handleCancel();
          }}
        />
      )}

      {step === "nfc-not-supported" && (
        <NfcNotSupportedContent
          workplaceName={workplaceName}
          onRetryNfc={() => {
            setStep("nfc-validation");
          }}
          onConvertToNonInstantPay={() => {
            setStep("accept-non-instant-pay");
          }}
          onCancel={() => {
            handleCancel();
          }}
        />
      )}

      {step === "requesting-clock-out" && (
        <BottomSheetLoadingContent
          title="Recording your clock out"
          description="This may take a moment"
        />
      )}

      {step === "clock-out-success" && (
        <TimekeepingActionSuccessContent
          title="Clocked out"
          description="We hope you had a great shift!"
          onClose={() => {
            skipWait();
          }}
        />
      )}

      {step === "accept-non-instant-pay" && (
        <AcceptNonInstantPayContent
          isMissedPunchRequestEnabled={shiftState.metadata.isMissedPunchRequestEnabled}
          onAccept={async () => {
            setStep("requesting-non-instant-pay");
            await convertToNonInstantPay();
          }}
          onCancel={() => {
            // When worker cancels the non-instant pay conversion, return them to the previous step
            // i.e. retry NFC validation or close (geofence was already checked before modal opened)
            if (isNfcEnabled) {
              setStep("nfc-not-supported");
            } else {
              handleCancel();
            }
          }}
        />
      )}

      {step === "requesting-non-instant-pay" && (
        <BottomSheetLoadingContent
          title="Converting to non-instant pay"
          description="This may take a moment"
        />
      )}
    </BottomSheet>
  );
}
